// Number and date formatting utilities

/**
 * Format a number as South African Rand currency
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return 'R0';
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Format a number with thousand separators
 * @param {number} num - The number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number string
 */
export const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return new Intl.NumberFormat('en-ZA', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

/**
 * Format distance in meters to a readable string
 * @param {number} meters - Distance in meters
 * @returns {string} Formatted distance string
 */
export const formatDistance = (meters) => {
  if (meters === null || meters === undefined || isNaN(meters)) return '-';
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
};

/**
 * Format travel time in minutes to a readable string
 * @param {number} minutes - Time in minutes
 * @returns {string} Formatted time string
 */
export const formatTravelTime = (minutes) => {
  if (minutes === null || minutes === undefined || isNaN(minutes)) return '-';
  if (minutes < 1) {
    return '< 1 min';
  }
  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
};

/**
 * Format a date to a readable string
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat('en-ZA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(d);
};

/**
 * Format a date to a short string
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted short date string
 */
export const formatShortDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
};

/**
 * Capitalize the first letter of each word
 * @param {string} str - The string to capitalize
 * @returns {string} Capitalized string
 */
export const capitalize = (str) => {
  if (!str) return '';
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Truncate a string to a maximum length
 * @param {string} str - The string to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated string
 */
export const truncate = (str, maxLength = 50) => {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 3) + '...';
};

/**
 * Parse coordinates from various formats
 * @param {string|number} value - The coordinate value
 * @returns {number|null} Parsed coordinate or null
 */
export const parseCoordinate = (value) => {
  if (value === null || value === undefined) return null;

  // Already a number
  if (typeof value === 'number') return value;

  // String handling
  const str = String(value).trim();

  // Check for comma as decimal separator (SA format)
  if (str.includes(',')) {
    // Could be "28,78907778" or "28°34'25.99"
    if (str.includes('°')) {
      return parseDMS(str);
    }
    // Replace comma with period for decimal
    return parseFloat(str.replace(',', '.'));
  }

  // DMS format like "25°34'25.99\"S"
  if (str.includes('°')) {
    return parseDMS(str);
  }

  // Regular decimal
  return parseFloat(str);
};

/**
 * Parse DMS (Degrees Minutes Seconds) format to decimal
 * @param {string} dms - DMS format string
 * @returns {number|null} Decimal degrees or null
 */
const parseDMS = (dms) => {
  const regex = /(\d+)[°\s]+(\d+)?['\s]*(\d+(?:\.\d+)?)?["\s]*([NSEW])/i;
  const match = dms.match(regex);

  if (!match) return null;

  const degrees = parseFloat(match[1]);
  const minutes = match[2] ? parseFloat(match[2]) : 0;
  const seconds = match[3] ? parseFloat(match[3]) : 0;
  const direction = match[4].toUpperCase();

  let decimal = degrees + minutes / 60 + seconds / 3600;

  if (direction === 'S' || direction === 'W') {
    decimal = -decimal;
  }

  return decimal;
};

/**
 * Calculate percentage
 * @param {number} value - The value
 * @param {number} total - The total
 * @returns {string} Percentage string
 */
export const formatPercentage = (value, total) => {
  if (!total || total === 0) return '0%';
  const percentage = (value / total) * 100;
  return `${percentage.toFixed(1)}%`;
};
